// modules
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useNewUser, RoleGroupItem, FieldErrors, CreateUserPayload } from "containers/NewUser/useNewUser";

// components
import Modal from "components/molecules/ModalContainer/ModalContainer";
import CountDownDialog from "components/molecules/ModalContainer/CountDownModalContainer";

// constants
import { MFA_STATE, Routes, USER_STATE, ViewEditUser, ViewMode, RoleManagement, NewUser } from "constants/global";

// redux
import { useAppDispatch, useAppSelector } from "app/hooks/hooks";
import {
	resetActivateUser,
	resetDeactivateUser,
	resetEditUser,
	resetResendTemporaryPassword,
	activateUser as activateUserAPI,
	deactivateUser as deactivateUserAPI,
	resendTemporaryPassword as resendTemporaryPasswordAPI,
	editUser as editUserAPI,
	getUserDetails,
	selectedUserDetailsSelector,
	resetSelectedAdminDetails,
	usersDataLoadingSelector,
	hasUnaurizedErrorSelector,
	getUserPermissionListSelector,
	updateCurrentGroup,
	getUserAuthDetails
} from "redux/userList/userSlice";

// utils
import { invalidateCookie } from "utils/token";

// types
import { GroupDetails } from "services/api/types";
import { PermissionGroup } from "containers/NewGroup/useNewGroup";

export type AdminData = {
	firstname: string;
	lastname: string;
	username: string;
	email: string;
};

type NewUserProps = {
	adminData: AdminData;
	permissionList: PermissionGroup[];
	is2FAChecked: boolean;
	groupsList?: Array<GroupDetails>;
	allGroupsList?: Array<GroupDetails>;
	selectedRoles: Array<string>;
	selectedGroups: Array<string>;
	currentSelectedGroup: RoleGroupItem;
	isCreateUserButtonEnabled?: boolean;
	fieldErrors: FieldErrors;
	hasMultipleLimitRoles: boolean;
	createUserFailure: (errorObj: Array<{ message: string }>) => void;
	setSelectedRoles: (arg: string[]) => void;
	setSelectedGroups: (arg: string[]) => void;
	validateFields: (arg: boolean) => void;
	handleGoBack?: () => void;
	proceedToCreateUser?: () => void;
	setCurrentSelectedGroup: (value: string, id: string) => void;
	handleRemoveSelectedGroup: (value: string) => void;
	handleAdminDataChange: (value: { id: string; value: string }) => void;
	handleEnable2FA: (value: boolean) => void;
	handleAddAnotherRole: () => void;
};

export type LocationState = {
	state: { username: string };
	pathname: string;
};

export const useViewEditUser = () => {
	const props: NewUserProps = useNewUser();
	const {
		adminData,
		permissionList,
		groupsList,
		is2FAChecked,
		currentSelectedGroup,
		selectedRoles,
		selectedGroups,
		isCreateUserButtonEnabled,
		fieldErrors,
		hasMultipleLimitRoles,
		allGroupsList,
		createUserFailure,
		setSelectedRoles,
		setSelectedGroups,
		validateFields,
		handleAddAnotherRole,
		setCurrentSelectedGroup,
		handleAdminDataChange,
		handleEnable2FA,
		handleRemoveSelectedGroup
	} = props;

	const navigate = useNavigate();
	const location = useLocation();
	const { state, pathname } = location as LocationState;
	const dispatch = useAppDispatch();

	const userDetails = useAppSelector(selectedUserDetailsSelector);
	const { data, errors } = userDetails || {};
	const loading = useAppSelector(usersDataLoadingSelector) === 1;
	const hasUnaurizedError = useAppSelector(hasUnaurizedErrorSelector);
	const userPermissionList = useAppSelector(getUserPermissionListSelector);

	const [mode, setMode] = useState<string>(ViewMode.READ);

	const selectedUsersDetails = useMemo(() => {
		if (userDetails?.data?.user_list) {
			return userDetails?.data?.user_list[0] || {};
		}
		return {};
	}, [userDetails]);

	const initialValues = selectedUsersDetails;

	const isUserActive = selectedUsersDetails?.active === USER_STATE.enabled || selectedUsersDetails?.active === "1";

	useEffect(() => {
		if (!state?.username) {
			navigate(Routes.ROLE_MANAGEMENT);
		}
		if (state?.username && pathname?.includes("edit-user")) {
			dispatch(getUserDetails({ username: state?.username }));
		}
		dispatch(updateCurrentGroup(RoleManagement.ALL_USERS_GROUP.group_code));
		return () => {
			dispatch(resetSelectedAdminDetails());
			dispatch(resetActivateUser());
			dispatch(resetDeactivateUser());
			dispatch(resetEditUser());
			dispatch(resetResendTemporaryPassword());
		};
	}, []);

	const navigateToLogin = useCallback(() => {
		invalidateCookie();
		navigate(Routes?.LOGIN, { state: { path: Routes.ROLE_MANAGEMENT } });
	}, [navigate]);

	// Change handler for checkbox
	const handleMode = useCallback(
		(value: string) => {
			setMode(value);
		},
		[setMode]
	);

	// Check whether user details are updated
	const isDetailsUpdated = useMemo(() => {
		const errs = Object.values(fieldErrors)?.filter((v) => !!v);

		if (errs?.length || hasMultipleLimitRoles) {
			return false;
		}

		const values = Object.values(adminData).filter((v) => !!v);

		if (values?.length || (selectedGroups?.length > 0 && selectedGroups?.length !== initialValues?.groups?.length)) {
			return true;
		}

		// If length are same but permissions are different
		const len = selectedGroups?.length;
		for (let i = 0; i < len; i++) {
			if (!initialValues?.groups?.includes(selectedGroups[i])) {
				return true;
			}
		}

		const is2FAUpdated = is2FAChecked ? MFA_STATE.enabled : MFA_STATE.disabled;

		if (is2FAUpdated !== initialValues.mfa_enabled) {
			return true;
		}

		return false;
	}, [adminData, selectedGroups, is2FAChecked, initialValues, fieldErrors, handleEnable2FA, handleAdminDataChange]);

	const handleGoBack = useCallback(() => {
		navigate(Routes?.ROLE_MANAGEMENT);
		dispatch(getUserAuthDetails());
	}, [navigate, dispatch, getUserAuthDetails]);

	const navigateToRoleManagement = useCallback(() => {
		navigate(Routes?.ROLE_MANAGEMENT);
	}, [navigate]);

	const deactivateUser = useCallback(async () => {
		if (selectedUsersDetails?.username) {
			const payload = { username: selectedUsersDetails?.username };

			try {
				const { errors: deactivateError } = await dispatch(deactivateUserAPI(payload)).unwrap();

				if (!deactivateError?.length) {
					CountDownDialog({ timer: 3, onTimeComplete: handleGoBack, title: ViewEditUser?.ACC_DEACTIVATE_SUCCESS });
				}
			} catch (error) {
				// axios error
				const err = error as any;
				if (err?.response?.status === 401) {
					navigateToLogin();
				}

				if (error) {
					CountDownDialog({
						timer: 3,
						errorIcon: true,
						onTimeComplete: handleGoBack,
						title: ViewEditUser?.ERROR_MESSAGE
					});
				}
			}
		}
	}, [selectedUsersDetails, CountDownDialog, handleGoBack]);

	const handleDeactivateUser = useCallback(() => {
		Modal({
			closable: true,
			type: "confirm",
			title: ViewEditUser.ACC_DEACTIVATE_WARNING,
			actionButton: {
				label: ViewEditUser.DEACTIVATE_BTN_LABEL,
				onClick: deactivateUser
			},
			cancelButton: {
				label: ViewEditUser.GO_BACK_LABEL,
				onClick: handleGoBack
			}
		});
	}, [Modal, deactivateUser, handleGoBack]);

	const activateUser = useCallback(async () => {
		if (selectedUsersDetails?.username) {
			const payload = { username: selectedUsersDetails?.username };

			try {
				const { errors: activateUserError } = await dispatch(activateUserAPI(payload)).unwrap();

				if (!activateUserError?.length) {
					CountDownDialog({
						timer: 3,
						onTimeComplete: handleGoBack,
						title: ViewEditUser?.ACC_ACTIVATE_SUCCESS
					});
				}
			} catch (error) {
				// axios error
				const err = error as any;
				if (err?.response?.status === 401) {
					navigateToLogin();
				}

				if (error) {
					CountDownDialog({
						timer: 3,
						onTimeComplete: handleGoBack,
						title: ViewEditUser?.ERROR_MESSAGE
					});
				}
			}
		}
	}, [selectedUsersDetails, activateUserAPI, dispatch, CountDownDialog, handleGoBack]);

	const resendPassword = useCallback(async () => {
		if (selectedUsersDetails?.username) {
			const payload = { username: selectedUsersDetails?.username };

			try {
				const { errors: resendPasswordError, message } = await dispatch(resendTemporaryPasswordAPI(payload)).unwrap();

				if (!resendPasswordError?.length) {
					CountDownDialog({
						timer: 3,
						onTimeComplete: handleGoBack,
						title: ViewEditUser?.ACC_RESEND_PASSWORD_SUCCESS,
						subtitle: message || ViewEditUser?.ACC_RESEND_PASSWORD_SUCCESS_RESPONSE
					});
				}
			} catch (error) {
				// axios error
				const err = error as any;
				if (err?.response?.status === 401) {
					navigateToLogin();
				}

				if (error) {
					const errorObj = err?.response?.data?.errors;
					const errorMessage = errorObj?.[0]?.message || ViewEditUser?.ERROR_MESSAGE;

					CountDownDialog({
						timer: 3,
						errorIcon: true,
						onTimeComplete: handleGoBack,
						title: errorMessage
					});
				}
			}
		}
	}, [selectedUsersDetails, resendTemporaryPasswordAPI, dispatch, CountDownDialog, handleGoBack, navigateToLogin]);

	const handleResendPassword = useCallback(() => {
		Modal({
			closable: true,
			type: "confirm",
			title: ViewEditUser.ACC_RESEND_PASSWORD_WARNING,
			actionButton: {
				label: ViewEditUser.RESEND_PASSWORD_BTN_LABEL,
				onClick: resendPassword
			},
			cancelButton: {
				label: ViewEditUser.GO_BACK_LABEL,
				onClick: handleGoBack
			}
		});
	}, [Modal, resendPassword, handleGoBack]);

	const lockUser = useCallback(() => {
		CountDownDialog({
			timer: 3,
			onTimeComplete: handleGoBack,
			title: ViewEditUser?.ACC_LOCK_SUCCESS
		});
	}, [handleGoBack, CountDownDialog]);

	const handleLockUser = useCallback(() => {
		Modal({
			type: "confirm",
			title: ViewEditUser?.ACC_LOCK_WARNING,
			actionButton: {
				label: ViewEditUser?.LOCK_BTN_LABEL,
				onClick: lockUser
			},
			cancelButton: {
				label: ViewEditUser?.GO_BACK_LABEL,
				onClick: handleGoBack
			}
		});
	}, [lockUser, handleGoBack, Modal]);

	const createPayload = useCallback(() => {
		const payload: CreateUserPayload = {
			username: `${initialValues?.username}`,
			email: `${adminData?.email || initialValues?.email}`,
			first_name: `${adminData?.firstname || initialValues?.first_name}`,
			last_name: `${adminData?.lastname || initialValues?.last_name}`,
			groups: selectedGroups,
			mfa_enabled: is2FAChecked ? MFA_STATE.enabled : MFA_STATE.disabled
		};

		if (initialValues?.tenant_code && initialValues?.tenant_name) {
			payload.tenant_code = initialValues?.tenant_code;
			payload.tenant_name = initialValues?.tenant_name;
		}

		return payload;
	}, [initialValues, adminData, selectedGroups, is2FAChecked]);

	// save updated changes
	const submitChanges = useCallback(async () => {
		const isFieldsValid = isDetailsUpdated && validateFields(false);

		if (isDetailsUpdated && isFieldsValid) {
			const payload: CreateUserPayload = createPayload();

			try {
				const { errors: editUserError } = await dispatch(editUserAPI(payload)).unwrap();

				if (!editUserError?.length) {
					CountDownDialog({
						timer: 3,
						onTimeComplete: handleGoBack,
						title: ViewEditUser?.ACC_EDIT_SUCCESS
					});
				}
			} catch (error) {
				// Added any as it is axios object
				const err = error as any;
				if (err?.response?.status === 401) {
					navigate(Routes.LOGIN, { state: { path: Routes?.ROLE_MANAGEMENT } });
				}

				const errorObj = err?.response?.data?.errors;
				createUserFailure(errorObj);
			}
		}
	}, [isDetailsUpdated, createPayload, handleGoBack, dispatch, editUserAPI, CountDownDialog, validateFields]);

	useMemo(() => {
		if (initialValues?.groups?.length) {
			let permissions: string[] = [];
			const groups: string[] = [];

			initialValues?.groups?.forEach((group: string) => {
				const groupObj = allGroupsList?.find(({ group_code }) => group_code === group);
				permissions = [...permissions, ...(groupObj?.permissions || [])];
				groups.push(group);
			});

			setSelectedRoles(permissions);
			setSelectedGroups(groups);
		}
		if (mode === ViewMode.EDIT) {
			handleEnable2FA(initialValues?.mfa_enabled === MFA_STATE.enabled);
		}
	}, [initialValues, groupsList, allGroupsList, mode, handleEnable2FA, setSelectedRoles, setSelectedGroups]);

	useEffect(() => {
		if (hasUnaurizedError) {
			// should comeback to role-management to avoid data loss.
			// e.g. we need username to fetch data onload that may not be available when navigating from login.
			navigateToLogin();
		}
	}, [hasUnaurizedError]);

	const disable2FACheckbox = useMemo(() => {
		if (mode === ViewMode.EDIT && !initialValues?.mfa_enabled) {
			return NewUser.DISABLE_CHECKBOX;
		}
		return "";
	}, [mode, initialValues]);

	const updatedGroupsList = useMemo(() => {
		if (groupsList && allGroupsList && initialValues?.tenant_code && initialValues?.tenant_name) {
			const tenantGroup = allGroupsList?.filter((group) => group?.group_code === NewUser.TENANT_GROUP);
			return [...groupsList, ...tenantGroup];
		}
		return groupsList;
	}, [groupsList, initialValues, allGroupsList]);

	const canResendPassword = useMemo(() => {
		if (!userPermissionList || !Array.isArray(userPermissionList)) {
			return false;
		}
		return !!userPermissionList.find(({ perm_code }) => perm_code === RoleManagement.CREATE_EDIT_ADMIN_ACCOUNT || perm_code === "CREATE_EDIT_TENANT_ACCOUNT");
	}, [userPermissionList]);

	return {
		initialValues,
		isUserActive,
		adminData,
		is2FAChecked,
		mode,
		permissionList,
		groupsList: updatedGroupsList,
		isDetailsUpdated,
		currentSelectedGroup,
		selectedRoles,
		selectedGroups,
		isCreateUserButtonEnabled,
		fieldErrors,
		loading: loading && !data && !errors?.length,
		backgroundLoading: loading && data,
		hasMultipleLimitRoles,
		disable2FACheckbox,
		handleMode,
		handleAdminDataChange,
		handleEnable2FA,
		handleGoBack,
		activateUser,
		handleDeactivateUser,
		handleAddAnotherRole,
		setCurrentSelectedGroup,
		handleRemoveSelectedGroup,
		handleLockUser,
		resendPassword,
		handleResendPassword,
		canResendPassword,
		submitChanges,
		validateFields,
		navigateToRoleManagement
	};
};
